import {getQuery, joinURL, withoutTrailingSlash, withQuery} from "ufo";
import type {ComputedRef} from "vue";

const PAGINATION_KEY = 'trimm.pagination.register' as const;

export enum PaginationType {
  Query = "Query",
  Path = "Path"
}


// Helper to fetch runtime configuration for pagination key
const usePaginationKey = () => {
  const runtimeConfig = useRuntimeConfig();
  return runtimeConfig.public.pagination.reservedRouteKey;
};

// Helper to fetch runtime configuration for pagination type
const usePaginationType = (): PaginationType => {
  const runtimeConfig = useRuntimeConfig();
  const type: string | undefined = runtimeConfig.pagination?.type;
  if (type) {
    const match = PaginationType[type as keyof typeof PaginationType];
    if (match) return match;
  }
  return PaginationType.Path;
};


/**
 * Removes pagination parameters from a given URI, preserving other query parameters.
 * Assists in handling URLs by stripping out pagination-related segments
 * while maintaining other parts of the query string intact.
 *
 * @param {string} uri The URI from which to remove pagination parameters.
 * @returns {string} The URI with pagination parameters removed, if any were present.
 */
export const stripPagination = (uri: string): string => {
  const uriWithoutSlash = withoutTrailingSlash(uri);
  const paginationKey = usePaginationKey();

  if (usePaginationType() === PaginationType.Query) {
    return withQuery(uri, {[paginationKey]: undefined})
  }

  const paginationIndex = uriWithoutSlash.lastIndexOf(`/${paginationKey}/`);
  const query = getQuery(uri);
  return paginationIndex >= 0 ? withQuery(uriWithoutSlash.substring(0, paginationIndex), query) : uri;
};


/**
 * Extracts the page number from the current route's path or query if pagination parameters are present.
 * It identifies if the route has pagination and, if so, extracts the page number from the URL
 * based on a predefined pagination key.
 */
export const usePageNumber = (): ComputedRef<number | undefined> => {
  const route = useRoute();
  const paginationKey = usePaginationKey();

  return computed(() => {
    let rawPage: string | undefined;
    if (usePaginationType() === PaginationType.Query) {
      const queryValue = route.query[paginationKey];
      rawPage = (Array.isArray(queryValue) ? queryValue[0] : queryValue) ?? undefined;
    } else {
      const pageRegex = new RegExp(`/${paginationKey}/(\\d+)$`);
      rawPage = route.path.match(pageRegex)?.[1];
    }

    return rawPage ? parseInt(rawPage, 10) : undefined;
  });
};

const createPaginationLink = (delta: number): ComputedRef<string> => {
  const route = useRoute();
  const pathWithoutPagination = stripPagination(route.path);
  const paginationKey = usePaginationKey();

  return computed(() => {
    const currentPage = usePageNumber().value ?? 1;
    if (usePaginationType() === PaginationType.Query) {
      return withQuery(pathWithoutPagination, {[paginationKey]: currentPage + delta});
    }
    return joinURL(pathWithoutPagination, paginationKey, String(currentPage + delta))
  });
}

/**
 * Defines and manages pagination within an  application based on the maximum number of pages allowed.
 * This function computes navigation links for previous and next pages and handles edge cases, such as exceeding
 * the maximum page limit by throwing a 404 error. Also updates the head of the page with the appropriate prev and next links
 *
 * @param {number} maxPages The maximum number of pages available for pagination.
 */
export const usePagination = (maxPages: number) => {

  const route = useRoute();
  const pathWithoutPagination = stripPagination(route.path);


  const prevPageLink = ref<string | undefined>();
  const nextPageLink = ref<string | undefined>();
  const _currentPage = ref<number>(1);

  watch(() => usePageNumber().value, (p: number | undefined) => {
    const currentPage = p ?? 1;
    _currentPage.value = currentPage;
    if (maxPages && currentPage > maxPages) {
      throw createError({
        statusCode: 404, fatal: true
      });
    }
    prevPageLink.value = currentPage > 2 ? createPaginationLink(-1).value : currentPage - 1 < 1 ? undefined : pathWithoutPagination;
    nextPageLink.value = (maxPages && currentPage + 1 > maxPages) ? undefined : createPaginationLink(1).value;
  }, {
    immediate: true
  })

  const pageLinks = computed(() => {
    return [{
      rel: 'prev', href: prevPageLink.value,
    }, {
      rel: 'next', href: nextPageLink.value
    }].filter(link => link.href)
  })

  useHead({
    link: pageLinks
  })

  /**
   * Register the presence of a pagination component to prevent 404 errors in absence of required pagination
   *
   * When {@link #usePaginationCheck} has been called this will allow us to notify it of the existence
   * of a pagination component, to which it can throw a 404 when no pagination component is used, but is passed in the URL.
   */
  const registerPagination = inject(PAGINATION_KEY);
  if (typeof registerPagination === 'function') {
    registerPagination();
  }


  return {
    pathWithoutPagination, currentPage: _currentPage, prevPageLink, nextPageLink
  };
};


/**
 * This composable provides pagination control by allowing a nested component to register a pagination mechanism
 * within the application and validating the current route for pagination requirements.
 * Also throws a 404 if the route contains pagination but no pagination component is registered.
 */
export const usePaginationCheck = () => {
  const paginationRegistered = ref(false);
  const nuxt = useNuxtApp();

  const pageNumber = usePageNumber();
  const hasPagination = toValue(pageNumber) != undefined;

  // TODO: Check if page example/{n} exists in CMS first?
  nuxt.hooks.hook('app:rendered', () => {
    if (hasPagination && !paginationRegistered.value) {
      throw createError({
        statusCode: 404, fatal: true
      })
    }
  })

  const register = () => {
    paginationRegistered.value = true;
  }
  // Provide the pagination registration function to the application context, using PROVIDE / INJECT to make sure it's only for the current tree/visit
  provide(PAGINATION_KEY, register);

  return {
    paginationRegistered: readonly(paginationRegistered),
  }

}

